import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { useOrganization } from "../organization/OrganizationProvider";
import { supabase } from "../lib/supabase";
import { downloadCsv } from "../lib/csv";

const navGroups = [
  {
    key: "operacion",
    label: "Operación",
    items: [
      ["/", "Inicio", null],
      ["/cotizaciones", "Cotizaciones", "quotations"],
      ["/ventas", "Ventas", "sales"],
      ["/compras", "Compras", "purchases"],
      ["/catalogo", "Catálogo", "catalog"],
      ["/maestros", "Maestros", "catalog"],
      ["/taller", "Taller", "workshop"],
      ["/inventario", "Inventario", "inventory"],
      ["/importaciones", "Importaciones", "purchases"],
      ["/clientes", "Clientes", "crm"],
      ["/activos", "Activos", "crm"],
    ],
  },
  {
    key: "finanzas",
    label: "Finanzas",
    items: [
      ["/contabilidad", "Contabilidad", "accounting"],
      ["/tesoreria", "Tesorería", "accounting"],
      ["/conciliacion-bancaria", "Conciliación", "accounting"],
      ["/auditoria", "Auditoría", "accounting"],
      ["/reportes", "Reportes", "accounting"],
      ["/fiscal", "Fiscal", "accounting"],
    ],
  },
  {
    key: "administracion",
    label: "Administración",
    items: [
      ["/configuracion", "Configuración", null],
      ["/super-admin", "Super Admin", null],
    ],
  },
] as const;

function moduleExportForPath(pathname: string) {
  if (pathname.startsWith("/cotizaciones")) return { module: "quotations", filename: "cotizaciones" };
  if (pathname.startsWith("/ventas")) return { module: "sales", filename: "ventas" };
  if (pathname.startsWith("/compras")) return { module: "purchases", filename: "compras" };
  if (pathname.startsWith("/catalogo")) return { module: "catalog", filename: "catalogo" };
  if (pathname.startsWith("/maestros")) return { module: "suppliers", filename: "maestros-proveedores" };
  if (pathname.startsWith("/taller")) return { module: "workshop", filename: "taller" };
  if (pathname.startsWith("/inventario")) return { module: "inventory", filename: "inventario" };
  if (pathname.startsWith("/importaciones")) return { module: "imports", filename: "importaciones" };
  if (pathname.startsWith("/clientes")) return { module: "clients", filename: "clientes" };
  if (pathname.startsWith("/activos")) return { module: "assets", filename: "activos" };
  if (pathname.startsWith("/contabilidad")) return { module: "accounting", filename: "contabilidad" };
  if (pathname.startsWith("/tesoreria")) return { module: "treasury", filename: "tesoreria" };
  if (pathname.startsWith("/conciliacion-bancaria")) return { module: "reconciliation", filename: "conciliacion" };
  if (pathname.startsWith("/auditoria")) return { module: "audit", filename: "auditoria" };
  if (pathname.startsWith("/fiscal")) return { module: "fiscal", filename: "fiscal" };
  return null;
}

function exportDateStamp() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Lima",
  }).format(new Date());
}

export function AppShell() {
  const { user, signOut } = useAuth();
  const {
    organizations,
    selectedOrganization,
    selectedOrganizationId,
    loading: organizationsLoading,
    selectOrganization,
    modulesLoading,
    isModuleEnabled,
  } = useOrganization();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    operacion: true,
    finanzas: false,
    administracion: false,
  });

  useEffect(() => {
    const activeGroup = navGroups.find((group) =>
      group.items.some(([to]) =>
        to === "/" ? location.pathname === "/" : location.pathname.startsWith(to),
      ),
    );

    if (activeGroup) {
      setOpenGroups({
        operacion: activeGroup.key === "operacion",
        finanzas: activeGroup.key === "finanzas",
        administracion: activeGroup.key === "administracion",
      });
    }
  }, [location.pathname]);

  const exportConfig = moduleExportForPath(location.pathname);

  async function exportCurrentModule() {
    if (!selectedOrganization || !exportConfig) return;

    setExportBusy(true);

    const { data, error } = await supabase.rpc("export_module_data", {
      p_organization_id: selectedOrganization.organizationId,
      p_module: exportConfig.module,
    });

    setExportBusy(false);

    if (error) {
      window.alert(`No se pudo exportar el módulo: ${error.message}`);
      return;
    }

    const rows = (Array.isArray(data) ? data : []) as Record<string, unknown>[];

    if (rows.length === 0) {
      window.alert("No hay registros para exportar.");
      return;
    }

    const normalized = rows.map((row) =>
      Object.fromEntries(
        Object.entries(row).map(([key, value]) => [
          key,
          value !== null && typeof value === "object"
            ? JSON.stringify(value)
            : value ?? "",
        ]),
      ),
    );

    const headers = Array.from(
      normalized.reduce((set, row) => {
        Object.keys(row).forEach((key) => set.add(key));
        return set;
      }, new Set<string>()),
    );

    downloadCsv(
      `${exportConfig.filename}-${exportDateStamp()}.csv`,
      headers,
      normalized,
    );
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <button
          className="menu-button"
          type="button"
          aria-label="Abrir navegación"
          onClick={() => setMenuOpen((value) => !value)}
        >
          ☰
        </button>

        <div className="topbar-title">
          <strong>Core SaaS</strong>
          <span className="topbar-subtitle">
            {organizationsLoading
              ? "Cargando empresa…"
              : selectedOrganization?.name ?? "Sin empresa activa"}
          </span>
        </div>

        <div className="topbar-actions">
          {exportConfig && selectedOrganization ? (
            <button
              className="ghost-button"
              type="button"
              disabled={exportBusy}
              onClick={() => void exportCurrentModule()}
            >
              {exportBusy ? "Exportando…" : "Exportar CSV"}
            </button>
          ) : null}
          <button className="ghost-button" type="button" onClick={() => void signOut()}>
            Salir
          </button>
        </div>
      </header>

      <aside className={menuOpen ? "sidebar sidebar-open" : "sidebar"}>
        <div className="brand">
          <div className="brand-mark">C</div>
          <div>
            <strong>Core SaaS</strong>
            <small>Panel operativo</small>
          </div>
        </div>

        <div className="organization-selector">
          <label htmlFor="organization-select">Empresa activa</label>
          <select
            id="organization-select"
            value={selectedOrganizationId ?? ""}
            disabled={organizationsLoading || organizations.length === 0}
            onChange={(event) => selectOrganization(event.target.value)}
          >
            {organizations.length === 0 ? (
              <option value="">Sin empresas disponibles</option>
            ) : null}
            {organizations.map((organization) => (
              <option
                key={organization.organizationId}
                value={organization.organizationId}
              >
                {organization.name}
              </option>
            ))}
          </select>
          {selectedOrganization ? (
            <small>
              {selectedOrganization.taxId
                ? `RUC ${selectedOrganization.taxId} · `
                : ""}
              {selectedOrganization.role}
            </small>
          ) : null}
        </div>

        <nav className="nav-list" aria-label="Navegación principal">
          {navGroups.map((group) => {
            const visibleItems = group.items.filter(([to, , moduleKey]) =>
              (to !== "/super-admin" ||
                Boolean(selectedOrganization?.isPlatformAdmin)) &&
              (moduleKey === null ||
                modulesLoading ||
                isModuleEnabled(moduleKey)),
            );

            if (visibleItems.length === 0) return null;

            return (
              <section className="nav-group" key={group.key}>
                <button
                  type="button"
                  className="nav-group-button"
                  aria-expanded={Boolean(openGroups[group.key])}
                  onClick={() =>
                    setOpenGroups((current) => ({
                      operacion:
                        group.key === "operacion" ? !current.operacion : false,
                      finanzas:
                        group.key === "finanzas" ? !current.finanzas : false,
                      administracion:
                        group.key === "administracion"
                          ? !current.administracion
                          : false,
                    }))
                  }
                >
                  <span>{group.label}</span>
                  <span aria-hidden="true">
                    {openGroups[group.key] ? "−" : "+"}
                  </span>
                </button>

                {openGroups[group.key] ? (
                  <div className="nav-submenu">
                    {visibleItems.map(([to, label]) => (
                      <NavLink
                        key={to}
                        to={to}
                        end={to === "/"}
                        onClick={() => setMenuOpen(false)}
                        className={({ isActive }) =>
                          isActive ? "nav-link nav-link-active" : "nav-link"
                        }
                      >
                        {label}
                      </NavLink>
                    ))}
                  </div>
                ) : null}
              </section>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <small>Sesión</small>
          <span>{user?.email ?? "Usuario"}</span>
        </div>
      </aside>

      {menuOpen ? (
        <button
          className="sidebar-backdrop"
          type="button"
          aria-label="Cerrar navegación"
          onClick={() => setMenuOpen(false)}
        />
      ) : null}

      <main className="main-content">
        <Outlet />
        <footer className="app-copyright">
          © 2026 - CORE SAAS - Powered by 360 Integral Solutions
        </footer>
      </main>
    </div>
  );
}
